import { create } from 'zustand'
import { TripResponse, TripWithDetailsResponse } from '@/shared/api'

interface TripState {
  // State
  trips: TripResponse[]
  currentTrip: TripWithDetailsResponse | null
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  } | null
  isLoading: boolean
  hasFetched: boolean
  error: string | null
  
  // Actions
  setTrips: (trips: TripResponse[]) => void
  setCurrentTrip: (trip: TripWithDetailsResponse | null) => void
  setPagination: (pagination: TripState['pagination']) => void
  setLoading: (loading: boolean) => void
  setHasFetched: (hasFetched: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
  
  // Trip operations
  addTrip: (trip: TripResponse) => void
  updateTrip: (tripId: string, updates: Partial<TripResponse>) => void
  removeTrip: (tripId: string) => void
  
  // Computed
  getTripById: (tripId: string) => TripResponse | null
  getTripsByStatus: (status: 'active' | 'upcoming' | 'past') => TripResponse[]
}

export const useTripStore = create<TripState>((set, get) => ({
  // Initial state
  trips: [],
  currentTrip: null,
  pagination: null,
  isLoading: false,
  hasFetched: false,
  error: null,

  // Actions
  setTrips: (trips: TripResponse[]) => {
    set({ trips })
  },

  setCurrentTrip: (trip: TripWithDetailsResponse | null) => {
    set({ currentTrip: trip })
  },

  setPagination: (pagination: TripState['pagination']) => {
    set({ pagination })
  },

  setLoading: (isLoading: boolean) => {
    set({ isLoading })
  },

  setHasFetched: (hasFetched: boolean) => {
    set({ hasFetched })
  },

  setError: (error: string | null) => {
    set({ error })
  },

  clearError: () => {
    set({ error: null })
  },

  // Trip operations
  addTrip: (trip: TripResponse) => {
    set(state => ({
      trips: [trip, ...(state.trips || [])]
    }))
  },

  updateTrip: (tripId: string, updates: Partial<TripResponse>) => {
    set(state => ({
      trips: (state.trips || []).map(trip => 
        trip.id === tripId ? { ...trip, ...updates } : trip
      ),
      currentTrip: state.currentTrip?.id === tripId 
        ? { ...state.currentTrip, ...updates } as TripWithDetailsResponse
        : state.currentTrip
    }))
  },

  removeTrip: (tripId: string) => {
    set(state => ({
      trips: (state.trips || []).filter(trip => trip.id !== tripId),
      currentTrip: state.currentTrip?.id === tripId ? null : state.currentTrip
    }))
  },

  // Computed values
  getTripById: (tripId: string) => {
    const { trips } = get()
    return trips.find(trip => trip.id === tripId) || null
  },

  getTripsByStatus: (status: 'active' | 'upcoming' | 'past') => {
    const { trips } = get()
    const now = new Date()
    
    return (trips || []).filter(trip => {
      if (!trip.start_date || !trip.end_date) return false
      
      const start = new Date(trip.start_date)
      const end = new Date(trip.end_date)
      
      switch (status) {
        case 'active':
          return now >= start && now <= end
        case 'upcoming':
          return now < start
        case 'past':
          return now > end
        default:
          return false
      }
    })
  }
}))

// Selector hooks for better performance
export const useTrips = () => useTripStore(state => state.trips)
export const useCurrentTrip = () => useTripStore(state => state.currentTrip)
export const usePagination = () => useTripStore(state => state.pagination)
export const useTripLoading = () => useTripStore(state => state.isLoading)
export const useTripHasFetched = () => useTripStore(state => state.hasFetched)
export const useTripError = () => useTripStore(state => state.error)

export const useTripActions = () => useTripStore(state => ({
  setTrips: state.setTrips,
  setCurrentTrip: state.setCurrentTrip,
  setPagination: state.setPagination,
  setLoading: state.setLoading,
  setError: state.setError,
  clearError: state.clearError,
  addTrip: state.addTrip,
  updateTrip: state.updateTrip,
  removeTrip: state.removeTrip
}))
